import React, { useState, useEffect, useRef } from 'react';
import './PostDetailModal.css';

const PostDetailModal = ({
  post,
  prevPost,
  nextPost,
  onClose,
  onEdit,
  onDelete,
  onNext,
  onPrev,
  onSlideChange,
  hasNext,
  hasPrev,
  initialSlide = 0
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialSlide);
  const [showInfo, setShowInfo] = useState(false);
  const [showControls, setShowControls] = useState(true); // UI 컨트롤 표시 여부
  const [isButtonPressed, setIsButtonPressed] = useState(false); // 버튼 눌림 상태
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [verticalOffset, setVerticalOffset] = useState(0); // 세로 슬라이더 offset
  const [disableTransition, setDisableTransition] = useState(false); // 세로 transition 일시 비활성화
  const [disableHorizontalTransition, setDisableHorizontalTransition] = useState(false); // 가로 transition 일시 비활성화
  const [containerWidth, setContainerWidth] = useState(0); // 컨테이너 너비
  
  // 핀치 투 줌 상태
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isPinching, setIsPinching] = useState(false);
  const [showZoomIndicator, setShowZoomIndicator] = useState(false);
  const pinchRef = useRef({ initialDistance: 0, initialScale: 1, lastX: 0, lastY: 0 });
  const zoomIndicatorTimeoutRef = useRef(null);
  const [infoDragOffset, setInfoDragOffset] = useState(0); // 정보 패널 드래그 offset
  const [isInfoDragging, setIsInfoDragging] = useState(false); // 정보 패널 드래그 중
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [hasDragged, setHasDragged] = useState(false);
  const [clickTarget, setClickTarget] = useState(null);
  const containerRef = useRef(null);
  const verticalContainerRef = useRef(null);
  const infoPanelRef = useRef(null);
  const hideControlsTimeoutRef = useRef(null);
  const videoRefs = useRef({}); // 비디오 요소들 저장
  const videoTouchRef = useRef({ startX: 0, startY: 0, startTime: 0 }); // 비디오 터치/마우스 추적
  
  const mediaFiles = post?.media || [];

  // 비디오 탭 핸들러 (재생/일시정지 + UI 토글)
  const handleVideoTap = (videoKey) => {
    const video = videoRefs.current[videoKey];
    if (video) {
      // 재생/일시정지 토글
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    }
    
    // UI 컨트롤 토글
    setShowControls(prev => !prev);
  };

  // 비디오 마우스 다운 (PC용)
  const handleVideoMouseDown = (e, videoKey) => {
    videoTouchRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startTime: Date.now(),
      videoKey
    };
    
    // 드래그 로직 시작
    setIsDragging(true);
    setStartX(e.clientX);
    setStartY(e.clientY);
    setDragOffset(0);
    setSwipeDirection(null);
    setHasDragged(false);
  };

  // 비디오 마우스 업 (PC용)
  const handleVideoMouseUp = (e, videoKey) => {
    const { startX, startY, startTime } = videoTouchRef.current;
    
    const diffX = Math.abs(e.clientX - startX);
    const diffY = Math.abs(e.clientY - startY);
    const diffTime = Date.now() - startTime;
    
    // 짧은 클릭(300ms 이하)이고 이동 거리가 작으면(10px 이하) 클릭으로 처리
    if (diffTime < 300 && diffX < 10 && diffY < 10) {
      if (e.target.tagName === 'VIDEO') {
        e.preventDefault();
        e.stopPropagation();
        handleVideoTap(videoKey);
        // 드래그 상태 초기화
        setIsDragging(false);
        setDragOffset(0);
        setVerticalOffset(0);
        setSwipeDirection(null);
        return;
      }
    }
    
    // 드래그인 경우 - handleDragEnd 호출
    if (hasDragged) {
      handleDragEnd();
    } else {
      // 드래그 상태 초기화
      setIsDragging(false);
      setDragOffset(0);
      setVerticalOffset(0);
      setSwipeDirection(null);
    }
  };

  // 비디오 터치 시작
  const handleVideoTouchStart = (e, videoKey) => {
    const touch = e.touches[0];
    videoTouchRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      videoKey
    };
    
    // 드래그 로직 시작 (상위 컨테이너 로직과 동일)
    setIsDragging(true);
    setStartX(touch.clientX);
    setStartY(touch.clientY);
    setDragOffset(0);
    setSwipeDirection(null);
    setHasDragged(false);
  };

  // 비디오 터치 끝
  const handleVideoTouchEnd = (e, videoKey) => {
    const touch = e.changedTouches[0];
    const { startX, startY, startTime } = videoTouchRef.current;
    
    const diffX = Math.abs(touch.clientX - startX);
    const diffY = Math.abs(touch.clientY - startY);
    const diffTime = Date.now() - startTime;
    
    // 짧은 터치(300ms 이하)이고 이동 거리가 작으면(10px 이하) 탭으로 처리
    if (diffTime < 300 && diffX < 10 && diffY < 10) {
      // 비디오 컨트롤바 영역이 아닌 경우에만 처리
      if (e.target.tagName === 'VIDEO') {
        e.preventDefault();
        e.stopPropagation();
        handleVideoTap(videoKey);
        // 드래그 상태 초기화
        setIsDragging(false);
        setDragOffset(0);
        setVerticalOffset(0);
        setSwipeDirection(null);
        return;
      }
    }
    
    // 드래그인 경우 - handleDragEnd 호출하지 않음 (상위에서 처리)
  };
  const currentMedia = mediaFiles[currentIndex];
  const isOwnerOrAdmin = post?.isOwner || post?.user?.isAdmin;

  // 배경 스크롤 잠금
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // ESC 키로 닫기
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Controls 자동 숨김 (3초 후) - 버튼 누르는 중에는 타이머 중지
  useEffect(() => {
    // 버튼을 누르고 있으면 타이머 시작하지 않음
    if (showControls && !isButtonPressed) {
      // 기존 타이머 제거
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
      
      // 3초 후 자동으로 숨김
      hideControlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
    
    // 버튼을 누르는 순간 타이머 제거
    if (isButtonPressed && hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }
    
    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, [showControls, isButtonPressed]);

  // initialSlide 동기화 (URL 변경, 브라우저 뒤로가기, 포스트 변경 등)
  // 포스트 변경 시 가로/세로 transition 모두 비활성화하여 "촤라락" 현상 방지
  useEffect(() => {
    if (post?.postId) {
      // transition 비활성화
      setDisableTransition(true);
      setDisableHorizontalTransition(true);
      
      // 즉시 위치 리셋
      setVerticalOffset(0);
      setCurrentIndex(initialSlide);
      
      // 줌 상태도 리셋
      setScale(1);
      setTranslateX(0);
      setTranslateY(0);
      
      // 다음 프레임에서 transition 다시 활성화
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setDisableTransition(false);
          setDisableHorizontalTransition(false);
        });
      });
    }
  }, [post?.postId, initialSlide]);

  // 줌 인디케이터 표시/숨기기
  useEffect(() => {
    if (scale !== 1) {
      setShowZoomIndicator(true);
      
      // 기존 타이머 제거
      if (zoomIndicatorTimeoutRef.current) {
        clearTimeout(zoomIndicatorTimeoutRef.current);
      }
      
      // 1.5초 후 숨기기
      zoomIndicatorTimeoutRef.current = setTimeout(() => {
        setShowZoomIndicator(false);
      }, 1500);
    } else {
      setShowZoomIndicator(false);
    }
    
    return () => {
      if (zoomIndicatorTimeoutRef.current) {
        clearTimeout(zoomIndicatorTimeoutRef.current);
      }
    };
  }, [scale]);

  // 컨테이너 너비 계산 및 업데이트
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    // 초기 계산
    updateContainerWidth();

    // 윈도우 리사이즈 시 재계산
    window.addEventListener('resize', updateContainerWidth);
    
    // 약간의 지연 후 다시 계산 (DOM 완전히 로드되도록)
    const timer = setTimeout(updateContainerWidth, 100);

    return () => {
      window.removeEventListener('resize', updateContainerWidth);
      clearTimeout(timer);
    };
  }, [post?.postId]);

  // 버튼 누름 시작 (타이머 일시정지)
  const handleButtonPressStart = () => {
    setIsButtonPressed(true);
  };

  // 버튼 누름 종료 (타이머 재시작)
  const handleButtonPressEnd = () => {
    setIsButtonPressed(false);
  };

  // 정보 패널 드래그 시작
  const [infoHasDragged, setInfoHasDragged] = useState(false);
  const [infoStartY, setInfoStartY] = useState(0);

  const handleInfoDragStart = (clientY) => {
    setIsInfoDragging(true);
    setInfoStartY(clientY);
    setInfoDragOffset(0);
    setInfoHasDragged(false);
  };

  // 정보 패널 드래그 이동
  const handleInfoDragMove = (clientY) => {
    if (!isInfoDragging) return;
    
    const diffY = clientY - infoStartY;
    
    // 10px 이상 움직였으면 드래그로 간주
    if (Math.abs(diffY) > 10) {
      setInfoHasDragged(true);
    }
    
    // 아래로만 드래그 가능 (위로는 제한)
    if (diffY > 0) {
      setInfoDragOffset(diffY);
    }
  };

  // 정보 패널 드래그 종료
  const handleInfoDragEnd = (e) => {
    if (!isInfoDragging) return;
    
    // 100px 이상 아래로 드래그하면 닫기
    if (infoDragOffset > 100) {
      setShowInfo(false);
    }
    
    setIsInfoDragging(false);
    setInfoDragOffset(0);
    setInfoHasDragged(false);
  };

  // 정보 패널 닫기 버튼
  const handleCloseInfo = () => {
    setShowInfo(false);
  };

  // 핀치 투 줌: 두 손가락 사이 거리 계산
  const getDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // 핀치 투 줌: 두 손가락 중심점 계산
  const getCenter = (touches) => {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    };
  };

  // 핀치 시작
  const handlePinchStart = (e) => {
    if (e.touches.length !== 2) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const distance = getDistance(e.touches);
    const center = getCenter(e.touches);
    
    pinchRef.current = {
      initialDistance: distance,
      initialScale: scale,
      lastX: center.x,
      lastY: center.y,
      startTranslateX: translateX,
      startTranslateY: translateY
    };
    
    setIsPinching(true);
  };

  // 핀치 이동
  const handlePinchMove = (e) => {
    if (!isPinching || e.touches.length !== 2) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const distance = getDistance(e.touches);
    const center = getCenter(e.touches);
    
    // 스케일 계산 (0.5 ~ 4배)
    let newScale = pinchRef.current.initialScale * (distance / pinchRef.current.initialDistance);
    newScale = Math.max(0.5, Math.min(4, newScale));
    
    // 팬(이동) 계산
    const deltaX = center.x - pinchRef.current.lastX;
    const deltaY = center.y - pinchRef.current.lastY;
    
    setScale(newScale);
    
    // 확대된 상태에서만 팬 가능
    if (newScale > 1) {
      setTranslateX(prev => prev + deltaX);
      setTranslateY(prev => prev + deltaY);
    }
    
    pinchRef.current.lastX = center.x;
    pinchRef.current.lastY = center.y;
  };

  // 핀치 종료
  const handlePinchEnd = (e) => {
    if (!isPinching) return;
    
    setIsPinching(false);
    
    // 1배 이하로 축소되면 원래 크기로 복구
    if (scale <= 1) {
      setScale(1);
      setTranslateX(0);
      setTranslateY(0);
    }
  };

  // 더블 탭으로 줌 토글 (2배 <-> 1배)
  const lastTapRef = useRef(0);
  const handleDoubleTap = (e) => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;
    
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      // 더블 탭 감지
      e.preventDefault();
      e.stopPropagation();
      
      if (scale > 1) {
        // 이미 확대된 상태면 원래 크기로
        setScale(1);
        setTranslateX(0);
        setTranslateY(0);
      } else {
        // 2배 확대
        setScale(2);
        // 탭한 위치를 중심으로 확대
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.touches ? e.touches[0].clientX : e.clientX;
        const y = e.touches ? e.touches[0].clientY : e.clientY;
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        setTranslateX((centerX - x));
        setTranslateY((centerY - y));
      }
    }
    
    lastTapRef.current = now;
  };

  // 확대된 상태에서 드래그로 팬
  const handleZoomedDrag = (clientX, clientY) => {
    if (scale <= 1) return false;
    
    const deltaX = clientX - startX;
    const deltaY = clientY - startY;
    
    setTranslateX(prev => prev + deltaX);
    setTranslateY(prev => prev + deltaY);
    setStartX(clientX);
    setStartY(clientY);
    
    return true; // 줌 상태에서 드래그 처리됨
  };

  // 마우스 휠로 줌 (PC)
  const handleWheel = (e) => {
    // 이미지 위에서만 동작
    if (!e.target.closest('.modal-media-slide') || e.target.tagName === 'VIDEO') {
      return;
    }
    
    e.preventDefault();
    
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    let newScale = scale + delta;
    newScale = Math.max(1, Math.min(4, newScale));
    
    // 1배로 돌아오면 위치도 리셋
    if (newScale <= 1) {
      setScale(1);
      setTranslateX(0);
      setTranslateY(0);
    } else {
      setScale(newScale);
    }
  };

  // 다음 슬라이드
  const handleNextSlide = () => {
    if (currentIndex < mediaFiles.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      onSlideChange?.(newIndex);
    }
  };

  // 이전 슬라이드
  const handlePrevSlide = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      onSlideChange?.(newIndex);
    }
  };

  // 마우스/터치 시작
  const handleDragStart = (clientX, clientY, target) => {
    // 확대 상태에서는 일반 드래그 시작하지 않음 (팬 처리)
    if (scale > 1) {
      setStartX(clientX);
      setStartY(clientY);
      return;
    }
    
    setIsDragging(true);
    setStartX(clientX);
    setStartY(clientY);
    setDragOffset(0);
    setSwipeDirection(null);
    setHasDragged(false);
    setClickTarget(target);
  };

  // 마우스/터치 이동
  const handleDragMove = (clientX, clientY) => {
    if (!isDragging) return;

    const diffX = clientX - startX;
    const diffY = clientY - startY;

    // 10px 이상 움직였으면 드래그로 간주
    if (Math.abs(diffX) > 10 || Math.abs(diffY) > 10) {
      setHasDragged(true);
    }

    // 첫 이동에서 방향 결정 (방향 잠금)
    if (!swipeDirection && (Math.abs(diffX) > 10 || Math.abs(diffY) > 10)) {
      if (Math.abs(diffX) > Math.abs(diffY)) {
        setSwipeDirection('horizontal');
      } else {
        setSwipeDirection('vertical');
      }
    }

    // 방향이 잠긴 후에는 해당 방향으로만 스와이프 가능
    // 가로 스와이프 처리 (좌우 슬라이드)
    if (swipeDirection === 'horizontal') {
      // 세로 움직임은 완전히 무시
      // 경계에서 저항 효과
      let resistance = 1;
      if ((currentIndex === 0 && diffX > 0) || (currentIndex === mediaFiles.length - 1 && diffX < 0)) {
        resistance = 0.3;
      }

      setDragOffset(diffX * resistance);
    }
    // 세로 스와이프 처리 (다음/이전 포스트)
    else if (swipeDirection === 'vertical') {
      // 가로 움직임은 완전히 무시
      // 경계에서 저항 효과
      let resistance = 1;
      if ((!hasPrev && diffY > 0) || (!hasNext && diffY < 0)) {
        resistance = 0.3;
      }
      setVerticalOffset(diffY * resistance);
    }
  };

  // 마우스/터치 종료
  const handleDragEnd = () => {
    if (!isDragging) return;

    // 드래그가 아닌 단순 클릭인 경우 (이동이 거의 없었음)
    const wasClick = !hasDragged && Math.abs(dragOffset) < 5;

    // 가로 스와이프 처리 (좌우 슬라이드)
    if (swipeDirection === 'horizontal') {
      const width = containerWidth || containerRef.current?.offsetWidth || 0;
      const threshold = width * 0.2;

      if (Math.abs(dragOffset) > threshold) {
        if (dragOffset < 0 && currentIndex < mediaFiles.length - 1) {
          // 왼쪽으로 스와이프 → 다음 슬라이드
          // 먼저 목표 위치(-width)로 자연스럽게 이동
          setDragOffset(-width);
          setIsDragging(false);
          // 애니메이션 완료 후 인덱스 변경
          setTimeout(() => {
            setDisableHorizontalTransition(true);
            setDragOffset(0);
            const newIndex = currentIndex + 1;
            setCurrentIndex(newIndex);
            onSlideChange?.(newIndex);
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setDisableHorizontalTransition(false);
              });
            });
            setSwipeDirection(null);
            setHasDragged(false);
            setClickTarget(null);
          }, 300);
          return;
        } else if (dragOffset > 0 && currentIndex > 0) {
          // 오른쪽으로 스와이프 → 이전 슬라이드
          // 먼저 목표 위치(+width)로 자연스럽게 이동
          setDragOffset(width);
          setIsDragging(false);
          // 애니메이션 완료 후 인덱스 변경
          setTimeout(() => {
            setDisableHorizontalTransition(true);
            setDragOffset(0);
            const newIndex = currentIndex - 1;
            setCurrentIndex(newIndex);
            onSlideChange?.(newIndex);
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setDisableHorizontalTransition(false);
              });
            });
            setSwipeDirection(null);
            setHasDragged(false);
            setClickTarget(null);
          }, 300);
          return;
        }
      }
    }
    // 세로 스와이프 처리 (다음/이전 포스트)
    else if (swipeDirection === 'vertical') {
      const threshold = 80; // 80px 이상 움직여야 포스트 변경
      const containerHeight = verticalContainerRef.current?.offsetHeight || window.innerHeight;

      if (Math.abs(verticalOffset) > threshold) {
        if (verticalOffset < 0 && hasNext) {
          // 위로 스와이프 → 다음 포스트
          // 먼저 목표 위치로 자연스럽게 이동
          setVerticalOffset(-containerHeight);
          setIsDragging(false);
          // 애니메이션 완료 후 다음 포스트로 전환
          setTimeout(() => {
            // transition 비활성화 후 즉시 위치 리셋
            setDisableTransition(true);
            setVerticalOffset(0);
            onNext?.();
            // 다음 프레임에서 transition 다시 활성화
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setDisableTransition(false);
              });
            });
            setSwipeDirection(null);
            setHasDragged(false);
            setClickTarget(null);
          }, 300);
          return;
        } else if (verticalOffset > 0 && hasPrev) {
          // 아래로 스와이프 → 이전 포스트
          // 먼저 목표 위치로 자연스럽게 이동
          setVerticalOffset(containerHeight);
          setIsDragging(false);
          // 애니메이션 완료 후 이전 포스트로 전환
          setTimeout(() => {
            // transition 비활성화 후 즉시 위치 리셋
            setDisableTransition(true);
            setVerticalOffset(0);
            onPrev?.();
            // 다음 프레임에서 transition 다시 활성화
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setDisableTransition(false);
              });
            });
            setSwipeDirection(null);
            setHasDragged(false);
            setClickTarget(null);
          }, 300);
          return;
        }
      }
    }

    // 클릭인 경우 controls 토글
    if (wasClick) {
      setShowControls(prev => !prev);
    }

    setIsDragging(false);
    setDragOffset(0);
    setVerticalOffset(0);
    setSwipeDirection(null);
    setHasDragged(false);
    setClickTarget(null);
  };

  // 마우스 이벤트
  const onMouseDown = (e) => {
    // 버튼이나 비디오 요소는 처리하지 않음 (네이티브 동작 유지)
    if (e.target.tagName === 'BUTTON' ||
        e.target.tagName === 'VIDEO' ||
        e.target.closest('.modal-slide-btn') ||
        e.target.closest('.modal-post-btn') ||
        e.target.closest('.modal-sidebar-btn') ||
        e.target.closest('video')) {
      return;
    }
    
    // 이미지에서 더블클릭 줌 감지
    if (e.target.tagName === 'IMG' || e.target.closest('.modal-media-slide')) {
      handleDoubleTap(e);
    }
    
    handleDragStart(e.clientX, e.clientY, e.target);
  };

  const onMouseMove = (e) => {
    // 확대된 상태에서는 팬(이동)으로 처리
    if (scale > 1 && isDragging) {
      if (handleZoomedDrag(e.clientX, e.clientY)) {
        return;
      }
    }
    
    handleDragMove(e.clientX, e.clientY);
  };

  const onMouseUp = (e) => {
    // 비디오 요소에서 드래그한 경우 처리 (클릭은 비디오 핸들러에서 처리)
    if (e.target.tagName === 'VIDEO' || e.target.closest('video')) {
      return; // 비디오 래퍼의 handleVideoMouseUp에서 처리
    }
    handleDragEnd();
  };

  const onMouseLeave = () => {
    if (isDragging) {
      handleDragEnd();
    }
  };

  // 터치 이벤트
  const onTouchStart = (e) => {
    // 버튼이나 비디오 요소는 처리하지 않음 (네이티브 동작 유지)
    if (e.target.tagName === 'BUTTON' ||
        e.target.tagName === 'VIDEO' ||
        e.target.closest('.modal-slide-btn') ||
        e.target.closest('.modal-post-btn') ||
        e.target.closest('.modal-sidebar-btn') ||
        e.target.closest('video')) {
      return;
    }
    
    // 핀치 줌 감지 (두 손가락)
    if (e.touches.length === 2) {
      handlePinchStart(e);
      return;
    }
    
    // 확대된 상태에서 더블 탭 감지
    if (e.target.tagName === 'IMG' || e.target.closest('.modal-media-slide')) {
      handleDoubleTap(e);
    }
    
    handleDragStart(e.touches[0].clientX, e.touches[0].clientY, e.target);
  };

  const onTouchMove = (e) => {
    // 핀치 줌 처리
    if (isPinching && e.touches.length === 2) {
      handlePinchMove(e);
      return;
    }
    
    // 확대된 상태에서는 팬(이동)으로 처리
    if (scale > 1 && e.touches.length === 1) {
      if (handleZoomedDrag(e.touches[0].clientX, e.touches[0].clientY)) {
        e.preventDefault();
        return;
      }
    }
    
    handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
  };

  const onTouchEnd = (e) => {
    // 핀치 종료
    if (isPinching) {
      handlePinchEnd(e);
      return;
    }
    
    // 비디오 요소에서 드래그한 경우 처리 (탭은 비디오 핸들러에서 처리)
    if (e.target.tagName === 'VIDEO' || e.target.closest('video')) {
      // 드래그였으면 handleDragEnd 호출
      if (hasDragged) {
        handleDragEnd();
      }
      return;
    }
    if (e.target.tagName !== 'BUTTON' && 
        !e.target.closest('.modal-slide-btn') &&
        !e.target.closest('.modal-post-btn') &&
        !e.target.closest('.modal-sidebar-btn')) {
      e.preventDefault();
    }
    handleDragEnd();
  };

  // Transform 계산 (가로 슬라이드용)
  const getTransformX = () => {
    // state의 containerWidth 사용 (초기 렌더링 시에도 올바른 값)
    const width = containerWidth || containerRef.current?.offsetWidth || 0;
    const baseOffset = -currentIndex * width;
    return baseOffset + (swipeDirection === 'horizontal' ? dragOffset : 0);
  };

  // Transform 계산 (세로 포스트 이동용)
  const getVerticalTransform = () => {
    // prevPost가 있으면 현재 포스트를 중앙에 배치하기 위해 실제 컨테이너 높이만큼 오프셋 적용
    const containerHeight = verticalContainerRef.current?.offsetHeight || window.innerHeight;
    const baseOffset = prevPost ? -containerHeight : 0;
    return baseOffset + verticalOffset;
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return '방금 전';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}분 전`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}시간 전`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}일 전`;
    
    return date.toLocaleDateString('ko-KR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // 해시태그 파싱
  const parseHashtags = (text) => {
    if (!text) return [];
    if (Array.isArray(text)) return text;
    if (typeof text === 'string') {
      const matches = text.match(/#[\w가-힣]+/g);
      return matches || [];
    }
    return [];
  };

  // 캡션에서 해시태그 제거
  const getCaptionWithoutHashtags = (text) => {
    if (!text) return '';
    if (typeof text !== 'string') return '';
    return text.replace(/#[\w가-힣]+/g, '').trim();
  };

  const hashtags = parseHashtags(post?.hashtags || '');
  const caption = getCaptionWithoutHashtags(post?.caption || '');

  // 단일 포스트 렌더링 함수
  const renderPostContent = (postData, slideIndex = 0) => {
    if (!postData) return null;
    
    const postMediaFiles = postData?.media || [];
    
    return (
      <div className="modal-media-container-inner">
        <div 
          className="modal-media-slider"
          style={{
            transform: `translateX(${postData.postId === post.postId ? getTransformX() : 0}px)`,
            transition: (isDragging && swipeDirection === 'horizontal' && postData.postId === post.postId) || disableHorizontalTransition ? 'none' : 'transform 0.3s ease'
          }}
        >
          {postMediaFiles.map((media, index) => (
            <div key={index} className="modal-media-slide">
              {media.mediaType === 'VIDEO' ? (
                <div 
                  className="modal-video-wrapper"
                  onMouseDown={(e) => handleVideoMouseDown(e, `${postData.postId}-${index}`)}
                  onMouseUp={(e) => handleVideoMouseUp(e, `${postData.postId}-${index}`)}
                  onTouchStart={(e) => handleVideoTouchStart(e, `${postData.postId}-${index}`)}
                  onTouchEnd={(e) => handleVideoTouchEnd(e, `${postData.postId}-${index}`)}
                >
                  <video
                    ref={(el) => { if (el) videoRefs.current[`${postData.postId}-${index}`] = el; }}
                    src={media.originalUrl}
                    poster={media.thumbnailUrl || media.originalUrl}
                    className="modal-media"
                    controls
                    playsInline
                    preload="metadata"
                    autoPlay={postData.postId === post.postId}
                    loop
                    draggable="false"
                  />
                </div>
              ) : (
                <img
                  src={media.originalUrl}
                  alt={`Slide ${index + 1}`}
                  className={`modal-media ${postData.postId === post.postId && index === currentIndex && scale > 1 ? 'zoomed' : ''}`}
                  draggable="false"
                  style={postData.postId === post.postId && index === currentIndex ? {
                    transform: `scale(${scale}) translate(${translateX / scale}px, ${translateY / scale}px)`,
                    transition: isPinching ? 'none' : 'transform 0.2s ease-out',
                    touchAction: scale > 1 ? 'none' : 'auto'
                  } : {}}
                />
              )}
            </div>
          ))}
        </div>

        {/* 슬라이드 버튼 (현재 포스트만) */}
        {postData.postId === post.postId && postMediaFiles.length > 1 && showControls && (
          <>
            {currentIndex > 0 && (
              <button 
                className={`modal-slide-btn modal-slide-btn-prev ${showControls ? 'show' : 'hide'}`}
                onClick={handlePrevSlide}
                onMouseDown={handleButtonPressStart}
                onMouseUp={handleButtonPressEnd}
                onMouseLeave={handleButtonPressEnd}
                onTouchStart={handleButtonPressStart}
                onTouchEnd={handleButtonPressEnd}
                aria-label="이전 슬라이드"
              >
                ‹
              </button>
            )}
            {currentIndex < postMediaFiles.length - 1 && (
              <button 
                className={`modal-slide-btn modal-slide-btn-next ${showControls ? 'show' : 'hide'}`}
                onClick={handleNextSlide}
                onMouseDown={handleButtonPressStart}
                onMouseUp={handleButtonPressEnd}
                onMouseLeave={handleButtonPressEnd}
                onTouchStart={handleButtonPressStart}
                onTouchEnd={handleButtonPressEnd}
                aria-label="다음 슬라이드"
              >
                ›
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="post-detail-modal-overlay">
      <div className="post-detail-modal">
        {/* 상단 헤더 */}
        <div className={`modal-header ${showControls ? 'show' : 'hide'}`}>
          <button 
            className="modal-close-btn" 
            onClick={onClose}
            onMouseDown={handleButtonPressStart}
            onMouseUp={handleButtonPressEnd}
            onMouseLeave={handleButtonPressEnd}
            onTouchStart={handleButtonPressStart}
            onTouchEnd={handleButtonPressEnd}
            aria-label="닫기"
          >
            ✕
          </button>
        </div>


        {/* 세로 슬라이더 영역 */}
        <div 
          ref={verticalContainerRef}
          className="modal-vertical-slider-container"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onWheel={handleWheel}
        >
          <div 
            className="modal-vertical-slider"
            style={{
              transform: `translateY(${getVerticalTransform()}px)`,
              transition: (isDragging && swipeDirection === 'vertical') || disableTransition ? 'none' : 'transform 0.3s ease'
            }}
          >
            {/* 이전 포스트 */}
            {prevPost && (
              <div className="modal-post-slide" ref={containerRef}>
                {renderPostContent(prevPost, 0)}
              </div>
            )}
            
            {/* 현재 포스트 */}
            <div className="modal-post-slide modal-post-slide-current" ref={!prevPost ? containerRef : null}>
              {renderPostContent(post, currentIndex)}
            </div>
            
            {/* 다음 포스트 */}
            {nextPost && (
              <div className="modal-post-slide">
                {renderPostContent(nextPost, 0)}
              </div>
            )}
          </div>

          {/* 다음/이전 포스트 버튼 */}
          {hasPrev && showControls && (
            <button 
              className={`modal-post-btn modal-post-btn-prev ${showControls ? 'show' : 'hide'}`}
              onClick={onPrev}
              onMouseDown={handleButtonPressStart}
              onMouseUp={handleButtonPressEnd}
              onMouseLeave={handleButtonPressEnd}
              onTouchStart={handleButtonPressStart}
              onTouchEnd={handleButtonPressEnd}
              aria-label="이전 포스트"
            >
              <span>↑</span>
              <span>이전</span>
            </button>
          )}
          {hasNext && showControls && (
            <button 
              className={`modal-post-btn modal-post-btn-next ${showControls ? 'show' : 'hide'}`}
              onClick={onNext}
              onMouseDown={handleButtonPressStart}
              onMouseUp={handleButtonPressEnd}
              onMouseLeave={handleButtonPressEnd}
              onTouchStart={handleButtonPressStart}
              onTouchEnd={handleButtonPressEnd}
              aria-label="다음 포스트"
            >
              <span>↓</span>
              <span>다음</span>
            </button>
          )}
        </div>

        {/* 우측 사이드바 */}
        <div className={`modal-sidebar ${showControls ? 'show' : 'hide'}`}>
          <button 
            className={`modal-sidebar-btn info-btn ${showInfo ? 'active' : ''}`}
            onClick={() => setShowInfo(!showInfo)}
            onMouseDown={handleButtonPressStart}
            onMouseUp={handleButtonPressEnd}
            onMouseLeave={handleButtonPressEnd}
            onTouchStart={handleButtonPressStart}
            onTouchEnd={handleButtonPressEnd}
            aria-label="정보 보기"
          >
            <div className="modal-sidebar-icon">⋯</div>
            <div className="modal-sidebar-text">정보</div>
          </button>

          {isOwnerOrAdmin && onEdit && (
            <button 
              className="modal-sidebar-btn"
              onClick={onEdit}
              onMouseDown={handleButtonPressStart}
              onMouseUp={handleButtonPressEnd}
              onMouseLeave={handleButtonPressEnd}
              onTouchStart={handleButtonPressStart}
              onTouchEnd={handleButtonPressEnd}
              aria-label="수정"
            >
              <div className="modal-sidebar-icon">✎</div>
              <div className="modal-sidebar-text">수정</div>
            </button>
          )}

          {isOwnerOrAdmin && onDelete && (
            <button 
              className="modal-sidebar-btn"
              onClick={onDelete}
              onMouseDown={handleButtonPressStart}
              onMouseUp={handleButtonPressEnd}
              onMouseLeave={handleButtonPressEnd}
              onTouchStart={handleButtonPressStart}
              onTouchEnd={handleButtonPressEnd}
              aria-label="삭제"
            >
              <div className="modal-sidebar-icon">🗑</div>
              <div className="modal-sidebar-text">삭제</div>
            </button>
          )}
        </div>

        {/* 줌 인디케이터 */}
        {showZoomIndicator && scale !== 1 && (
          <div className="modal-zoom-indicator" key={scale}>
            {Math.round(scale * 100)}%
          </div>
        )}

        {/* 하단 작성자 정보 */}
        <div className="modal-author-minimal">
          <span className="modal-author-name-minimal">
            {post?.user?.username || 'Unknown'}
          </span>
          <span className="modal-date-minimal">
            {formatDate(post?.createdAt)}
          </span>
        </div>

        {/* 정보 패널 */}
        {showInfo && (
          <div 
            ref={infoPanelRef}
            className={`modal-info-panel visible`}
            style={{
              transform: `translateY(${infoDragOffset}px)`,
              transition: isInfoDragging ? 'none' : 'transform 0.3s ease'
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              if (e.target.closest('.modal-info-handle')) {
                handleInfoDragStart(e.clientY);
              }
            }}
            onMouseMove={(e) => handleInfoDragMove(e.clientY)}
            onMouseUp={(e) => handleInfoDragEnd(e)}
            onMouseLeave={(e) => handleInfoDragEnd(e)}
            onTouchStart={(e) => {
              e.stopPropagation();
              if (e.target.closest('.modal-info-handle')) {
                handleInfoDragStart(e.touches[0].clientY);
              }
            }}
            onTouchMove={(e) => handleInfoDragMove(e.touches[0].clientY)}
            onTouchEnd={(e) => {
              e.stopPropagation();
              handleInfoDragEnd(e);
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-info-header">
              <div 
                className="modal-info-handle" 
                role="button"
                tabIndex={0}
                aria-label="드래그하여 닫기"
              />
              <button 
                className="modal-info-close-btn"
                onClick={handleCloseInfo}
                aria-label="정보 닫기"
              >
                ✕
              </button>
            </div>
          
          <div className="modal-info-content">
            <div className="modal-author-row">
              <span className="modal-author-name">
                {post?.user?.username || 'Unknown'}
              </span>
              <span className="modal-date-text">
                {formatDate(post?.createdAt)}
              </span>
            </div>

            {caption && (
              <div className="modal-caption-section">
                <p className="modal-caption-text">{caption}</p>
              </div>
            )}

            {hashtags.length > 0 && (
              <div className="modal-hashtags-section">
                {hashtags.map((tag, index) => (
                  <span key={index} className="modal-hashtag">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {isOwnerOrAdmin && (
              <div className="modal-actions">
                {onEdit && (
                  <button 
                    className="modal-action-btn edit"
                    onClick={onEdit}
                  >
                    수정하기
                  </button>
                )}
                {onDelete && (
                  <button 
                    className="modal-action-btn delete"
                    onClick={onDelete}
                  >
                    삭제하기
                  </button>
                )}
              </div>
            )}
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostDetailModal;
